import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faXmark, faRightLeft, faNoteSticky, faShuffle } from '@fortawesome/free-solid-svg-icons';
import { isPendingId, listsApi, productsApi, type ListItemDetail, type ProductSummary, type ScanResult, type ShoppingListSummary, type ShoppingSummary } from '../lib/domain';
import { useScope } from '../lib/hooks';
import { ApiError, OfflineError } from '../lib/api';
import { processOutbox } from '../lib/offline';
import { normalizeQuantity, quantityMin, quantityStep, useUnits } from '../lib/units';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { ToggleSwitch } from '../components/ToggleSwitch';
import { ImageThumbs } from '../components/ImageGallery';
import { MoveToListControl } from '../components/MoveToListControl';
import { groupByCategory } from '../lib/groupByCategory';
import { SHOPPING_PROGRESS_EVENT, type ShoppingProgress } from '../components/ShopMenu';

function errorMessage(err: unknown): string {
  if (err instanceof OfflineError) return 'Zum Abschließen ist eine Internetverbindung nötig.';
  return err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen. Bitte erneut versuchen.';
}

interface PurchaseDraft {
  productId: string;
  productName: string;
  listItemId?: string;
  quantity: number;
  unit: string;
  unitPrice: string;
  kgPrice: string;
  isPromo: boolean;
  priceMissing: boolean;
  // Noch offene Sollmenge der Position (null = Produkt steht nicht auf der Liste).
  plannedRemaining: number | null;
  // Produkt steht nicht auf der Liste: Position wird beim Buchen mit der gekauften Menge angelegt.
  addToList: boolean;
  // Ersatzprodukt: ersetzt diese Position (wird nach dem Buchen als nicht erhältlich markiert).
  replaces?: { itemId: string; name: string; remaining: number; unit: string };
}

interface SubstitutePicker {
  item: ListItemDetail;
  remaining: number;
  query: string;
  results: ProductSummary[];
  busy: boolean;
}

// Gesamtpreis = Einzelpreis x (tatsächlich erhaltene) Menge, kaufmännisch auf Cent gerundet.
function draftTotal(draft: PurchaseDraft): number | null {
  const unitPrice = Number(draft.unitPrice.replace(',', '.'));
  if (!draft.unitPrice || !Number.isFinite(unitPrice)) return null;
  return Math.round(unitPrice * draft.quantity * 100) / 100;
}

const STATUS_LABEL: Record<string, string> = { OPEN: 'offen', PARTIAL: 'teilweise', DONE: 'erledigt', UNAVAILABLE: 'nicht erhältlich' };

export function ShoppingModePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  const [scope] = useScope();
  const units = useUnits(scope);
  const [list, setList] = useState<Awaited<ReturnType<typeof listsApi.detail>>['data']['list'] | null>(null);
  const [summary, setSummary] = useState<ShoppingSummary | null>(null);
  const [manualEan, setManualEan] = useState('');
  const [draft, setDraft] = useState<PurchaseDraft | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [ambiguous, setAmbiguous] = useState<ListItemDetail[] | null>(null);
  const [ambiguousProduct, setAmbiguousProduct] = useState<{ id: string; name: string } | null>(null);
  const [aggregatePicker, setAggregatePicker] = useState<{ item: ListItemDetail; products: ProductSummary[] } | null>(null);
  const [substitutePicker, setSubstitutePicker] = useState<SubstitutePicker | null>(null);
  const [substituteEan, setSubstituteEan] = useState('');
  const [message, setMessage] = useState('');
  const [noteDialog, setNoteDialog] = useState<{ name: string; note: string } | null>(null);
  const [otherLists, setOtherLists] = useState<ShoppingListSummary[]>([]);
  const isArchived = list?.status === 'ARCHIVED';
  const draftRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    const detail = await listsApi.detail(id);
    const sum = await listsApi.summary(id, detail.data.list);
    setList(detail.data.list);
    setSummary(sum.summary);
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Issue #2: Positionen sollen auch im Einkaufsmodus auf eine andere,
  // noch offene Liste verschoben werden können.
  useEffect(() => {
    if (!scope) return;
    listsApi
      .list(scope, 'OPEN')
      .then((r) => setOtherLists(r.data.lists.filter((l) => l.id !== id)))
      .catch(() => undefined);
  }, [scope, id, list]);

  // Fortschritt (ausstehende Produkte) und Listenname in die Kopfzeile melden.
  useEffect(() => {
    if (!list || !id) return;
    const detail: ShoppingProgress = {
      listId: id,
      name: list.name || list.storeBranch?.name || 'Einkaufsliste',
      pending: list.items.filter((i) => i.status === 'OPEN' || i.status === 'PARTIAL').length,
      total: list.items.length,
    };
    window.dispatchEvent(new CustomEvent(SHOPPING_PROGRESS_EVENT, { detail }));
  }, [list, id]);

  async function moveItem(itemId: string, targetListId: string) {
    if (!id) return;
    setMessage('');
    try {
      await listsApi.moveItem(id, itemId, targetListId);
      await refresh();
    } catch (err) {
      setMessage(errorMessage(err));
    }
  }

  // Nutzeranfrage: der Kaufdialog wird oberhalb der (ggf. langen) Positions-
  // liste eingefügt und war damit nach Klick auf "Kaufen" oft außerhalb des
  // sichtbaren Bereichs - jetzt wird direkt dorthin gescrollt.
  useEffect(() => {
    if (draft) requestAnimationFrame(() => draftRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [draft]);

  async function handleEan(ean: string) {
    if (!id || !scope) return;
    setMessage('');
    const result: ScanResult = await listsApi.scan(id, ean);
    if (result.status === 'MATCHED') {
      openDraft(result.product.id, result.product.name, result.listItem);
    } else if (result.status === 'AMBIGUOUS') {
      setAmbiguousProduct({ id: result.product.id, name: result.product.name }); // gescanntes Produkt bekommt den Preis, auch bei Sammelproduktposition
      setAmbiguous(result.listItems);
    } else if (result.status === 'NOT_LISTED') {
      openDraft(result.product.id, result.product.name, undefined, { addToList: true }); // SHOP-004
    } else {
      // SHOP-005: unbekannte EAN -> automatisch aus externen Daten anlegen
      // (OpenFoodFacts), statt in einer Sackgasse zu enden. Findet sich auch
      // extern nichts, bleibt nur die manuelle Anlage.
      try {
        const resolved = await productsApi.resolveByEan(ean, scope.startsWith('group:') ? scope.slice(6) : undefined);
        if (resolved.found === 'created' && resolved.product) {
          setMessage(`Neues Produkt „${resolved.product.name}“ aus externen Daten angelegt und zur Liste hinzugefügt.`);
          await addToListAndOpenDraft(resolved.product.id, resolved.product.name);
        } else if (resolved.found === 'local' && resolved.product) {
          await addToListAndOpenDraft(resolved.product.id, resolved.product.name);
        } else {
          setMessage(`Unbekannte EAN ${ean}. Keine externen Daten gefunden - bitte Produkt manuell anlegen.`);
        }
      } catch (err) {
        setMessage(errorMessage(err));
      }
    }
  }

  // Neu angelegtes bzw. nicht auf der Liste stehendes Produkt: wird beim Kauf
  // als Position eingetragen, damit es nach dem Kauf auch in der Liste erscheint.
  async function addToListAndOpenDraft(productId: string, productName: string) {
    // Die Position wird erst beim Buchen angelegt - mit der tatsächlich gekauften Menge (auch 2 oder 3).
    openDraft(productId, productName, undefined, { addToList: true });
  }

  // Sammelproduktposition ohne EAN-Scan: verbundene Produkte zur Auswahl anbieten,
  // damit auch Produkte ohne EAN als gekauft gebucht werden können.
  async function pickAggregateProduct(item: ListItemDetail) {
    if (!scope || !item.aggregateId) return;
    setMessage('');
    try {
      const { products } = await productsApi.list(scope, { aggregateId: item.aggregateId });
      const active = products.filter((p) => !p.archived);
      if (active.length === 0) {
        setMessage(`Dem Sammelprodukt „${item.aggregate?.name ?? ''}“ sind keine Produkte zugeordnet.`);
        return;
      }
      setAggregatePicker({ item, products: active });
    } catch (err) {
      setMessage(errorMessage(err));
    }
  }

  function openDraft(
    productId: string,
    productName: string,
    listItem?: ListItemDetail,
    opts: { addToList?: boolean; replaces?: PurchaseDraft['replaces'] } = {},
  ) {
    if (isArchived) {
      setMessage('Diese Liste ist abgeschlossen. Bitte zuerst wieder eröffnen.');
      return;
    }
    setDraftError(null);
    // Nutzeranfrage: im Kaufdialog soll standardmäßig die noch offene
    // (Soll- minus bereits gekaufte/nicht-erhältliche) Menge vorausgewählt
    // sein, nicht immer nur 1 Stück.
    const remaining = listItem
      ? Number(listItem.targetQuantity) - Number(listItem.fulfilledQuantity) - Number(listItem.unavailableQuantity)
      : opts.replaces?.remaining ?? 1;
    const unit = listItem?.unit ?? opts.replaces?.unit ?? 'STUECK';
    const quantity = normalizeQuantity(remaining > 0 ? remaining : 1, units.allowDecimal(unit));
    setDraft({
      productId,
      productName,
      listItemId: listItem?.id,
      quantity,
      unit,
      unitPrice: '',
      kgPrice: '',
      isPromo: false,
      priceMissing: false,
      plannedRemaining: listItem ? remaining : null,
      addToList: !!opts.addToList && !listItem,
      replaces: opts.replaces,
    });
    setAmbiguous(null);
    setAggregatePicker(null);
    setSubstitutePicker(null);
  }

  // Teilkäufe: Menge im Kaufdialog per +/- um 1 anpassen.
  function stepDraftQuantity(direction: 1 | -1) {
    setDraft((d) => {
      if (!d) return d;
      const allowDecimal = units.allowDecimal(d.unit);
      const next = Math.round((d.quantity + direction) * 1000) / 1000;
      return { ...d, quantity: Math.max(quantityMin(allowDecimal), normalizeQuantity(next, allowDecimal)) };
    });
  }

  async function submitDraft() {
    if (!draft || !id) return;
    setDraftError(null);
    setDraftBusy(true);
    const body: Record<string, unknown> = {
      productId: draft.productId,
      listItemId: isPendingId(draft.listItemId) ? undefined : draft.listItemId, // lokal angelegte Position kennt der Server noch nicht
      quantity: draft.quantity,
      unit: draft.unit,
      isPromo: draft.isPromo,
      allowExceed: true, // Mehrmenge wurde im Dialog angezeigt und bewusst bestätigt
    };
    if (draft.priceMissing) {
      body.priceMissing = true;
    } else {
      body.unitPrice = Number(draft.unitPrice);
      const kg = Number(draft.kgPrice.replace(',', '.'));
      if (draft.kgPrice.trim() && Number.isFinite(kg) && kg >= 0) body.kgPrice = kg;
    }
    try {
      if (draft.addToList && !draft.listItemId) {
        // Nicht gelistetes Produkt / Ersatzprodukt: Position mit der tatsächlich gekauften Menge anlegen.
        await listsApi.addItem(id, { productId: draft.productId, targetQuantity: draft.quantity, unit: draft.unit });
        const detail = await listsApi.detail(id);
        const item = detail.data.list.items.find((i) => i.productId === draft.productId && i.unit === draft.unit && i.status !== 'DONE' && i.status !== 'UNAVAILABLE');
        if (item && !isPendingId(item.id)) body.listItemId = item.id;
      }
      await listsApi.recordPurchaseItem(id, body);
      if (draft.replaces) {
        // Ersatz gekauft -> Originalprodukt als nicht erhältlich markieren (mit Hinweis).
        try {
          await listsApi.markUnavailable(id, draft.replaces.itemId, draft.replaces.remaining, `Ersatz: ${draft.productName}`);
        } catch (err) {
          setMessage(`Ersatz gebucht, aber „${draft.replaces.name}“ konnte nicht als nicht erhältlich markiert werden: ${errorMessage(err)}`);
        }
      }
      setDraft(null);
      await refresh();
    } catch (err) {
      setDraftError(errorMessage(err));
    } finally {
      setDraftBusy(false);
    }
  }

  async function markUnavailable(item: ListItemDetail) {
    if (!id || isArchived) return;
    const remaining = Number(item.targetQuantity) - Number(item.fulfilledQuantity) - Number(item.unavailableQuantity);
    if (remaining <= 0) return;
    setMessage('');
    try {
      await listsApi.markUnavailable(id, item.id, remaining);
      await refresh();
    } catch (err) {
      setMessage(errorMessage(err));
    }
  }

  function openSubstitutePicker(item: ListItemDetail) {
    const remaining = Number(item.targetQuantity) - Number(item.fulfilledQuantity) - Number(item.unavailableQuantity);
    if (remaining <= 0 || isArchived) return;
    setSubstituteEan('');
    setMessage('');
    setSubstitutePicker({ item, remaining, query: '', results: [], busy: false });
  }

  async function searchSubstitute(query: string) {
    setSubstitutePicker((p) => (p ? { ...p, query, busy: query.trim().length > 0 } : p));
    if (!scope || !query.trim()) {
      setSubstitutePicker((p) => (p ? { ...p, results: [], busy: false } : p));
      return;
    }
    try {
      const { products } = await productsApi.list(scope, { query: query.trim() });
      setSubstitutePicker((p) => (p && p.query === query ? { ...p, results: products.filter((x) => !x.archived).slice(0, 15), busy: false } : p));
    } catch (err) {
      setMessage(errorMessage(err));
      setSubstitutePicker((p) => (p ? { ...p, busy: false } : p));
    }
  }

  // Ersatzprodukt per EAN: lokales Produkt verwenden, sonst wie beim normalen Scan aus externen Daten anlegen.
  async function scanSubstitute(ean: string) {
    if (!scope || !ean.trim()) return;
    setMessage('');
    try {
      const resolved = await productsApi.resolveByEan(ean.trim(), scope.startsWith('group:') ? scope.slice(6) : undefined);
      if ((resolved.found === 'local' || resolved.found === 'created') && resolved.product) {
        if (resolved.product.id === substitutePicker?.item.productId) {
          setMessage('Das gescannte Produkt ist das Originalprodukt. Bitte ein anderes Produkt als Ersatz wählen.');
          return;
        }
        chooseSubstitute(resolved.product);
      } else {
        setMessage(`Unbekannte EAN ${ean}. Keine externen Daten gefunden - bitte Produkt über die Suche wählen oder manuell anlegen.`);
      }
    } catch (err) {
      setMessage(errorMessage(err));
    }
  }

  function chooseSubstitute(product: ProductSummary) {
    if (!substitutePicker) return;
    const { item, remaining } = substitutePicker;
    openDraft(product.id, product.name, undefined, {
      addToList: true,
      replaces: { itemId: item.id, name: item.product?.name ?? item.aggregate?.name ?? '', remaining, unit: item.unit },
    });
  }

  // Alle Positionen sind bearbeitet (gekauft, teilweise oder nicht erhältlich): Liste kann abgeschlossen werden.
  async function completeShopping() {
    if (!id) return;
    setMessage('');
    setCompleting(true);
    try {
      await processOutbox(); // lokal vorgemerkte Käufe zuerst synchronisieren
      await listsApi.complete(id, true);
      navigate(`/lists/${id}/summary`);
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setCompleting(false);
    }
  }

  async function reopen() {
    if (!id) return;
    try {
      await listsApi.reopen(id);
      await refresh();
    } catch (err) {
      setMessage(errorMessage(err));
    }
  }

  if (!list || !summary) return <p>Lade…</p>;
  if (list.memoScopeKey) return <Navigate to={`/lists/${list.id}`} replace />; // Merkliste hat keinen Einkaufsmodus

  return (
    <div className="stack">
      <h1>Einkaufsmodus: {list.name || 'Einkaufsliste'}</h1>
      {list.storeBranch && (
        <p className="muted">
          {list.storeBranch.chain.name} - {list.storeBranch.name}
        </p>
      )}
      <Link to={`/lists/${id}`}>← Zur Liste</Link>

      {isArchived && (
        <div className="card stack" style={{ borderColor: 'var(--color-warning)' }}>
          <p>
            <span className="badge warn">Abgeschlossen</span> Diese Liste ist bereits als Einkauf archiviert. Zum Weitereinkaufen
            oder Korrigieren zuerst wieder eröffnen.
          </p>
          <button className="primary" onClick={reopen}>
            Liste wieder eröffnen
          </button>
        </div>
      )}

      <div className="card row between">
        <div>
          <strong>{Number(summary.totalAmount).toFixed(2)} €</strong>
          <div className="muted">Gesamtsumme laufend</div>
        </div>
        <div>
          {summary.missingPriceCount > 0 && <span className="badge warn">{summary.missingPriceCount} fehlende Preise</span>}
        </div>
      </div>

      <div className="card stack">
        <BarcodeScanner disabled={isArchived} onDetect={handleEan} onError={setMessage} />
        <div className="row">
          <input placeholder="EAN manuell eingeben" value={manualEan} onChange={(e) => setManualEan(e.target.value)} disabled={isArchived} />
          <button onClick={() => handleEan(manualEan)} disabled={!manualEan || isArchived}>
            Suchen
          </button>
        </div>
        {message && (
          <p className="muted" role="status">
            {message}
          </p>
        )}
      </div>

      {ambiguous && (
        <div className="card stack">
          <h2>Mehrere passende Positionen</h2>
          {ambiguous.map((item) => (
            <button key={item.id} onClick={() => openDraft(ambiguousProduct?.id ?? item.productId ?? '', ambiguousProduct?.name ?? item.product?.name ?? '', item)}>
              {item.product?.name ?? item.aggregate?.name}
            </button>
          ))}
        </div>
      )}

      {aggregatePicker && (
        <div className="card stack" role="dialog" aria-label="Produkt auswählen">
          <h2>Welches Produkt wurde gekauft?</h2>
          <p className="muted">Sammelprodukt „{aggregatePicker.item.aggregate?.name}“</p>
          {aggregatePicker.products.map((p) => (
            <button key={p.id} onClick={() => openDraft(p.id, p.name, aggregatePicker.item)}>
              {p.name}
              {p.brand ? ` (${p.brand})` : ''}
            </button>
          ))}
          <button onClick={() => setAggregatePicker(null)}>Abbrechen</button>
        </div>
      )}

      {substitutePicker && (
        <div className="card stack" role="dialog" aria-label="Ersatzprodukt auswählen">
          <h2>Ersatzprodukt für {substitutePicker.item.product?.name ?? substitutePicker.item.aggregate?.name}</h2>
          <p className="muted">
            Das Originalprodukt wird als nicht erhältlich markiert, das Ersatzprodukt als gekauft auf die Liste gesetzt.
          </p>
          <input
            type="search"
            autoFocus
            placeholder="Ersatzprodukt suchen"
            value={substitutePicker.query}
            onChange={(e) => void searchSubstitute(e.target.value)}
            aria-label="Ersatzprodukt suchen"
          />
          <BarcodeScanner onDetect={scanSubstitute} onError={setMessage} />
          <div className="row">
            <input placeholder="EAN manuell eingeben" value={substituteEan} onChange={(e) => setSubstituteEan(e.target.value)} aria-label="EAN des Ersatzprodukts" />
            <button onClick={() => void scanSubstitute(substituteEan)} disabled={!substituteEan}>
              Suchen
            </button>
          </div>
          {message && (
            <p className="muted" role="status">
              {message}
            </p>
          )}
          {substitutePicker.busy && <p className="muted">Suche…</p>}
          {!substitutePicker.busy && substitutePicker.query.trim() && substitutePicker.results.length === 0 && <p className="muted">Keine Produkte gefunden.</p>}
          {substitutePicker.results
            .filter((p) => p.id !== substitutePicker.item.productId)
            .map((p) => (
              <button key={p.id} onClick={() => chooseSubstitute(p)}>
                {p.name}
                {p.brand ? ` (${p.brand})` : ''}
              </button>
            ))}
          <button onClick={() => setSubstitutePicker(null)}>Abbrechen</button>
        </div>
      )}

      {draft && (
        <div className="card stack" role="dialog" aria-label="Kaufdialog" ref={draftRef}>
          <h2>Kaufdialog: {draft.productName}</h2>
          <div className="row">
            <button
              onClick={() => stepDraftQuantity(-1)}
              disabled={draft.quantity <= quantityMin(units.allowDecimal(draft.unit))}
              aria-label="Menge verringern"
              title="Menge verringern"
            >
              −
            </button>
            <input
              type="number"
              min={quantityMin(units.allowDecimal(draft.unit))}
              step={quantityStep(units.allowDecimal(draft.unit))}
              style={{ width: 100 }}
              value={draft.quantity}
              onChange={(e) => setDraft({ ...draft, quantity: normalizeQuantity(e.target.value, units.allowDecimal(draft.unit)) })}
              aria-label="Menge"
            />
            <button onClick={() => stepDraftQuantity(1)} aria-label="Menge erhöhen" title="Menge erhöhen">
              +
            </button>
            <span>{units.label(draft.unit)}</span>
          </div>
          {draft.replaces && (
            <p className="badge warn" role="status">
              Ersatz für „{draft.replaces.name}“ – wird nach dem Buchen als nicht erhältlich markiert.
            </p>
          )}
          {draft.plannedRemaining !== null && draft.quantity > Math.max(draft.plannedRemaining, 0) && (
            <p className="badge warn" role="alert">
              {draft.plannedRemaining > 0
                ? `Achtung: Geplant waren nur noch ${draft.plannedRemaining} ${units.label(draft.unit)}, du buchst ${draft.quantity}. Das ist trotzdem möglich.`
                : `Achtung: Diese Position ist bereits vollständig erledigt. Du buchst zusätzlich ${draft.quantity} ${units.label(draft.unit)}. Das ist trotzdem möglich.`}
            </p>
          )}
          {draft.addToList && !draft.replaces && (
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              Nicht auf der Liste geplant – wird mit der hier gewählten Menge zur Liste hinzugefügt (auch mehrfach möglich).
            </p>
          )}

          <div className="field">
            <label htmlFor="unit-price">Einzelpreis (€ pro {units.label(draft.unit)})</label>
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <input
                id="unit-price"
                type="number"
                min={0}
                step="0.01"
                placeholder={draft.priceMissing ? 'wird später nachgetragen' : '0,00'}
                value={draft.unitPrice}
                disabled={draft.priceMissing}
                onChange={(e) => setDraft({ ...draft, unitPrice: e.target.value })}
                style={{ flex: 1 }}
              />
              <label className="row" style={{ gap: 6, flexShrink: 0, whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={draft.priceMissing}
                  onChange={(e) => setDraft({ ...draft, priceMissing: e.target.checked })}
                />
                Preis unbekannt
              </label>
            </div>
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              {draft.priceMissing
                ? 'Der Preis kann später in der Kassenübersicht nachgetragen werden.'
                : draftTotal(draft) !== null
                  ? `Gesamtpreis: ${draftTotal(draft)!.toFixed(2)} € (${draft.quantity} × ${Number(draft.unitPrice).toFixed(2)} €). Menge oben anpassen, wenn nicht alles erhältlich war.`
                  : 'Der Gesamtpreis wird aus Einzelpreis × Menge berechnet. Menge oben anpassen, wenn nicht alles erhältlich war.'}
            </p>
          </div>

          <div className="field">
            <label htmlFor="kg-price">Preis pro kg (optional)</label>
            <input
              id="kg-price"
              type="number"
              min={0}
              step="0.01"
              placeholder="z. B. 12,90"
              value={draft.kgPrice}
              disabled={draft.priceMissing}
              onChange={(e) => setDraft({ ...draft, kgPrice: e.target.value })}
            />
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              Auch bei Angabe in Stück/Packung speicherbar, z. B. für den Preisvergleich.
            </p>
          </div>

          <div className="row between" style={{ cursor: 'pointer' }} onClick={() => setDraft({ ...draft, isPromo: !draft.isPromo })}>
            <span>
              Aktionspreis
              <div className="muted" style={{ fontSize: '0.8rem' }}>
                Einschalten, wenn dieser Preis ein zeitlich begrenztes Angebot ist.
              </div>
            </span>
            <ToggleSwitch checked={draft.isPromo} onChange={(v) => setDraft({ ...draft, isPromo: v })} label="Aktionspreis" />
          </div>

          {draftError && (
            <p className="error-text" role="alert">
              {draftError}
            </p>
          )}
          <div className="row">
            <button className="primary" onClick={submitDraft} disabled={draftBusy || (!draft.priceMissing && !draft.unitPrice)}>
              {draftBusy ? 'Speichert…' : 'Als gekauft buchen'}
            </button>
            <button onClick={() => setDraft(null)} disabled={draftBusy}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {noteDialog && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Einkaufsnotiz"
          onClick={() => setNoteDialog(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}
        >
          <div className="card stack" style={{ maxWidth: 480, width: '100%', background: 'var(--color-surface, #fff)' }} onClick={(e) => e.stopPropagation()}>
            <h2>{noteDialog.name}</h2>
            <p style={{ whiteSpace: 'pre-wrap' }}>{noteDialog.note}</p>
            <button className="primary" autoFocus onClick={() => setNoteDialog(null)}>
              Schließen
            </button>
          </div>
        </div>
      )}

      <h2>Positionen</h2>
      {groupByCategory(list.items).map((group) => {
        const isFinished = (i: { status: string }) => i.status === 'DONE' || i.status === 'UNAVAILABLE';
        const categoryDone = group.items.every(isFinished);
        // Erledigte und teilweise gekaufte Artikel ans Kategorie-Ende (stabile Sortierung behält die übrige Reihenfolge)
        const isDimmed = (i: { status: string }) => isFinished(i) || i.status === 'PARTIAL';
        const sortedItems = [...group.items].sort((a, b) => Number(isDimmed(a)) - Number(isDimmed(b)));
        return (
          <div key={group.key} className="stack" style={{ marginBottom: 'var(--space-3)' }}>
            <div className="row between">
              <h3 style={{ margin: 0 }}>{group.name}</h3>
              {categoryDone && <span className="badge ok">✓ erledigt</span>}
            </div>
            <ul className="list-reset stack">
              {sortedItems.map((item) => (
                <li key={item.id} className="card stack" style={isDimmed(item) ? { opacity: 0.55 } : undefined}>
                  <div className="row between">
                    <div className="row" style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                      <ImageThumbs images={item.product?.images ?? []} size={48} onlyFirst alt={item.product?.name ?? ''} />
                    <div>
                      <strong>{item.product?.name ?? item.aggregate?.name}</strong>
                      {!item.productId && item.aggregate && <span className="badge" style={{ marginLeft: 8 }}>Sammelprodukt</span>}
                      {(item.product ?? item.aggregate)?.shoppingNote && (
                        <button
                          type="button"
                          style={{ marginLeft: 8 }}
                          aria-label="Einkaufsnotiz anzeigen"
                          title="Einkaufsnotiz anzeigen"
                          onClick={() =>
                            setNoteDialog({
                              name: item.product?.name ?? item.aggregate?.name ?? '',
                              note: (item.product ?? item.aggregate)?.shoppingNote ?? '',
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faNoteSticky} />
                        </button>
                      )}
                      {!item.productId && item.aggregate?.description && <div className="muted">{item.aggregate.description}</div>}
                      <div className="muted">
                        {item.fulfilledQuantity}/{item.targetQuantity} {units.label(item.unit)} · {STATUS_LABEL[item.status] ?? item.status}
                      </div>
                      {item.unavailableNote && <div className="badge warn">{item.unavailableNote}</div>}
                    </div>
                    </div>
                    <div className="row">
                      {item.status === 'OPEN' || item.status === 'PARTIAL' ? (
                        <>
                          <button
                            onClick={() =>
                              item.productId
                                ? openDraft(item.productId, item.product?.name ?? '', item)
                                : void pickAggregateProduct(item)
                            }
                            disabled={isArchived}
                            aria-label="Kaufen"
                            title={item.productId ? 'Kaufen' : 'Sammelprodukt: gekauftes Produkt auswählen'}
                          >
                            <FontAwesomeIcon icon={faCartShopping} />
                          </button>
                          <button onClick={() => markUnavailable(item)} disabled={isArchived} aria-label="Gibt es nicht" title="Gibt es nicht">
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                          {item.productId && (
                            <button onClick={() => openSubstitutePicker(item)} disabled={isArchived} aria-label="Ersatzprodukt kaufen" title="Ersatzprodukt kaufen">
                              <FontAwesomeIcon icon={faShuffle} />
                            </button>
                          )}
                          {item.status === 'OPEN' && (
                            <MoveToListControl
                              lists={otherLists}
                              onMove={(targetListId) => moveItem(item.id, targetListId)}
                              disabled={isArchived}
                              triggerIcon={<FontAwesomeIcon icon={faRightLeft} />}
                              triggerLabel="Verschieben"
                            />
                          )}
                        </>
                      ) : (
                        <span className="badge ok">{item.status === 'DONE' ? 'Vollständig gekauft' : 'Nicht erhältlich'}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {!isArchived && list.items.length > 0 && list.items.every((i) => i.status !== 'OPEN') && (
        <div className="card stack" style={{ borderColor: 'var(--color-primary, #1d5c3e)' }}>
          <h2>Alle Artikel bearbeitet</h2>
          <p className="muted">Alle Artikel sind gekauft, teilweise oder nicht erhältlich. Die Liste kann jetzt abgeschlossen werden.</p>
          <button className="primary" onClick={completeShopping} disabled={completing}>
            {completing ? 'Schließt ab…' : 'Liste abschließen'}
          </button>
        </div>
      )}
    </div>
  );
}
